import React, { useState, useEffect } from "react";
import styles from "./MasonryGrid.module.css";
import Image from "../common/Image/Image";

export interface Photo {
  id: string;
  src: string;
  srcSet?: string;
  alt: string;
  title: string;
  category: string;
  width: number;
  height: number;
  aspectRatio?: number | string;
  project?: string;
  projectName?: string;
}

interface MasonryGridProps {
  photos: Photo[];
  columns?: number;
  onPhotoClick?: (photo: Photo) => void;
  staggerDelay?: number;
}

export const MasonryGrid: React.FC<MasonryGridProps> = ({
  photos,
  columns = 4,
  onPhotoClick,
  staggerDelay = 80,
}) => {
  const [visibleItems, setVisibleItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    const timer = setTimeout(() => {
      photos.forEach((photo, index) => {
        setTimeout(() => {
          setVisibleItems((prev) => new Set(prev).add(photo.id));
        }, index * staggerDelay);
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [photos, staggerDelay]);

  const getColumnPhotos = () => {
    const columns_array: Photo[][] = Array.from({ length: columns }, () => []);
    photos.forEach((photo, index) => {
      columns_array[index % columns].push(photo);
    });
    return columns_array;
  };

  const columnPhotos = getColumnPhotos();

  return (
    <div
      className={styles.masonryGrid}
      style={
        {
          "--columns": columns,
        } as React.CSSProperties
      }
    >
      {columnPhotos.map((column, colIndex) => (
        <div key={colIndex} className={styles.masonryColumn}>
          {column.map((photo) => {
            const isVisible = visibleItems.has(photo.id);

            return (
              <div
                key={photo.id}
                className={`
                  ${styles.photoItem}
                  ${isVisible ? styles.visible : ""}
                `}
                onClick={() => onPhotoClick?.(photo)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onPhotoClick?.(photo);
                  }
                }}
                aria-label={`View ${photo.title}`}
                data-photo-id={photo.id}
              >
                <div className={styles.imageWrapper}>
                  <Image
                    src={photo.src}
                    alt={photo.alt}
                    title={photo.title}
                    width={photo.width}
                    height={photo.height}
                    className={styles.photoImage}
                    loading="lazy"
                    decoding="async"
                    priority={false}
                    placeholder="empty"
                    project={photo.project || "root"} // Pass project ID
                    style={{
                      aspectRatio: photo.aspectRatio || "4/3",
                    }}
                  />
                  <div className={styles.photoOverlay}>
                    <div className={styles.photoInfo}>
                      <span className={styles.photoCategory}>
                        {photo.projectName}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};
